// src/b2b/components/PriceCalendar.jsx
import React, { useMemo } from 'react';

export default function PriceCalendar({
  data, // { code, prices:[{date, price}], nights, ... }
  onSelect, // (payload) => void  // quand l’utilisateur clique un départ
  selectedDate, // string ISO 'YYYY-MM-DD' (optionnel)
}) {
  if (!data) return null;

  // Groupement par mois pour l’affichage type “Kepler”
  const groups = useMemo(() => {
    const byMonth = new Map(); // key 'YYYY-MM' -> [{dateObj, iso, labelDay, labelWeekday, price}]
    for (const p of data.prices || []) {
      const d = new Date(p.date);
      if (Number.isNaN(+d)) continue;
      const iso = d.toISOString().slice(0, 10);
      const ym = iso.slice(0, 7);
      const weekday = d.toLocaleDateString('fr-FR', { weekday: 'long' }); // lundi
      const day = d.toLocaleDateString('fr-FR', { day: '2-digit' }); // 01
      const item = {
        dateObj: d,
        iso,
        price: Number(p.price),
        labelWeekday: weekday.charAt(0).toUpperCase() + weekday.slice(1), // capitalise
        labelDay: day,
        // départ + nuits = retour
        endIso:
          data.nights && !Number.isNaN(+data.nights)
            ? new Date(d.getTime() + Number(data.nights) * 86400000).toISOString().slice(0, 10)
            : null,
      };
      if (!byMonth.has(ym)) byMonth.set(ym, []);
      byMonth.get(ym).push(item);
    }
    // tri croissant dans chaque mois
    for (const arr of byMonth.values()) {
      arr.sort((a, b) => a.dateObj - b.dateObj);
    }
    // on renvoie un tableau [{monthLabel, key, items}]
    return [...byMonth.entries()].map(([ym, items]) => {
      const [y, m] = ym.split('-');
      const monthLabel = new Date(`${ym}-01`).toLocaleDateString('fr-FR', {
        month: 'long',
        year: 'numeric',
      });
      return { key: ym, monthLabel: monthLabel.toUpperCase(), items };
    });
  }, [data]);

  return (
    <div className="price-cal">
      <style>{css}</style>
      {groups.map(({ key, monthLabel, items }) => (
        <div key={key} className="pc-month">
          <div className="pc-month-header">
            <span>{monthLabel}</span>
            <span className="pc-note">Prix ttc/pers*</span>
          </div>

          {items.map((it) => {
            const isSelected = selectedDate === it.iso;
            return (
              <button
                type="button"
                key={it.iso}
                className={`pc-row ${isSelected ? 'is-selected' : ''}`}
                onClick={() =>
                  onSelect?.({
                    date: it.iso,
                    endDate: it.endIso,
                    price: it.price,
                    nights: data.nights ?? null,
                  })
                }
                aria-pressed={isSelected ? 'true' : 'false'}
              >
                <div className="pc-col pc-col--day">
                  <div className="pc-weekday">{it.labelWeekday}</div>
                  <div className="pc-day">{it.labelDay}</div>
                </div>

                <div className="pc-col pc-col--arrow">→</div>

                <div className="pc-col pc-col--retour">
                  {/* si tu veux, calcule et affiche le jour de retour */}
                  {it.endIso && (
                    <>
                      <div className="pc-weekday">
                        {new Date(it.endIso).toLocaleDateString('fr-FR', {
                          weekday: 'long',
                        })}
                      </div>
                      <div className="pc-day">
                        {new Date(it.endIso).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                        })}
                      </div>
                    </>
                  )}
                </div>

                <div className="pc-col pc-col--price">
                  {Number.isFinite(it.price) ? (
                    <strong>{it.price.toFixed(2)}€</strong>
                  ) : (
                    <span>—</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/* ====== CSS in JS pour un rendu proche de ta maquette ====== */
const css = `
.price-cal { display: grid; gap: 12px; }
.pc-month { background:#fff; border-radius:10px; overflow:hidden; box-shadow:0 2px 10px rgba(0,0,0,.06); }
.pc-month-header { display:flex; justify-content:space-between; padding:10px 14px; background:#f46a77; color:#fff; font-weight:600; }
.pc-note { opacity:.9; font-size:.85rem; }
.pc-row { display:grid; grid-template-columns: 1fr 40px 1fr 120px; align-items:center; gap:10px; width:100%; padding:14px; border-top:1px solid #f2f2f5; background:#fff; cursor:pointer; }
.pc-row:hover { background:#f8faff; }
.pc-row.is-selected { outline:2px solid #00105f; outline-offset:-2px; background:#eef3ff; }
.pc-col--day, .pc-col--retour { text-align:center; }
.pc-weekday { font-size:.9rem; color:#4b4f5c; text-transform:capitalize; }
.pc-day { font-size:1.3rem; font-weight:700; color:#0a0d2d; }
.pc-col--arrow { text-align:center; font-weight:700; color:#4b4f5c; }
.pc-col--price { text-align:right; font-size:1.1rem; color:#0a0d2d; }
@media (max-width: 640px){
  .pc-row{ grid-template-columns: 1fr 30px 1fr 100px; padding:12px; }
}
`;
